import React, { useState } from 'react';
import {
  Stack,
  Paper,
  Title,
  Text,
  Button,
  Group,
  ActionIcon,
  Checkbox,
  Divider
} from '@mantine/core';
import { IconBell, IconTrash, IconRefresh } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import PageTitle from 'components/PageTitle';
import Badge from 'components/Badge';
import { pathnames } from 'routes/yearlyCalendar';

interface Notification {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  type: 'schedule' | 'module' | 'system';
  scheduleId?: string;
}

const NotificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'Schedule Plan Updated',
      message: 'The schedule plan "New Lecture Series" has been updated. Check the changes.',
      timestamp: '2025-04-27T10:30:00',
      isRead: false,
      type: 'schedule',
      scheduleId: 'schedule-1'
    },
    {
      id: '2',
      title: 'New Module Added',
      message: 'A new module "Artificial Intelligence Basics" has been added to your schedule.',
      timestamp: '2025-04-26T14:45:00',
      isRead: false,
      type: 'module',
      scheduleId: 'schedule-2'
    },
    {
      id: '3',
      title: 'Reminder: Schedule Starting Soon',
      message: 'The schedule "Data Analysis Workshop" will start in 3 days.',
      timestamp: '2025-04-25T09:15:00',
      isRead: true,
      type: 'schedule',
      scheduleId: 'schedule-3'
    },
    {
      id: '4',
      title: 'System Maintenance',
      message: 'The system will be under maintenance on April 30, 2025, from 11:00 PM to 2:00 AM.',
      timestamp: '2025-04-24T16:20:00',
      isRead: true,
      type: 'system'
    }
  ]);

  const [selectAll, setSelectAll] = useState(false);

  const handleToggleSelectAll = () => {
    setSelectAll(!selectAll);
    setNotifications(
      notifications.map((notification) => ({
        ...notification,
        isSelected: !selectAll
      }))
    );
  };

  const handleMarkAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({
        ...notification,
        isRead: true
      }))
    );
  };

  const handleDeleteAll = () => {
    setNotifications([]);
  };

  const handleDeleteNotification = (id: string) => {
    setNotifications(notifications.filter((notification) => notification.id !== id));
  };

  const handleViewSchedule = (scheduleId?: string) => {
    if (scheduleId) {
      navigate(pathnames.schedulePlanDetail.replace(':id', scheduleId));
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'schedule':
        return 'blue';
      case 'module':
        return 'green';
      case 'system':
        return 'orange';
      default:
        return 'gray';
    }
  };

  return (
    <Stack gap='md'>
      <PageTitle
        title='Notifications'
        description='Stay updated with the latest changes and announcements about your schedules.'
      />

      <Paper p='lg' radius='md' withBorder>
        <Stack gap='md'>
          <Group justify='space-between'>
            <Group>
              <Checkbox checked={selectAll} onChange={handleToggleSelectAll} label='Select All' />
            </Group>

            <Group>
              <Button
                variant='subtle'
                leftSection={<IconRefresh size={16} />}
                onClick={handleMarkAsRead}
              >
                Mark All as Read
              </Button>

              <Button
                variant='outline'
                color='red'
                leftSection={<IconTrash size={16} />}
                onClick={handleDeleteAll}
              >
                Delete All
              </Button>
            </Group>
          </Group>

          <Divider />

          {notifications.length === 0 ? (
            <Stack align='center' py={40}>
              <IconBell size={48} color='#7B2183' opacity={0.5} />
              <Title order={4}>No Notifications</Title>
              <Text c='dimmed'>You're all caught up! There are no notifications to display.</Text>
            </Stack>
          ) : (
            notifications.map((notification) => (
              <Paper
                key={notification.id}
                p='md'
                withBorder
                bg={notification.isRead ? undefined : '#f8f9fa'}
              >
                <Group justify='space-between' align='flex-start'>
                  <Group align='flex-start'>
                    <Checkbox checked={selectAll} readOnly />

                    <Stack gap='xs'>
                      <Group>
                        <Title order={5}>{notification.title}</Title>
                        <Badge
                          styleVariant='subtle'
                          bg={getNotificationTypeColor(notification.type)}
                        >
                          {notification.type}
                        </Badge>
                        {!notification.isRead && <Badge bg='red'>New</Badge>}
                      </Group>

                      <Text>{notification.message}</Text>

                      <Group>
                        <Text size='sm' c='dimmed'>
                          {formatTimestamp(notification.timestamp)}
                        </Text>

                        {notification.scheduleId && (
                          <Button
                            variant='subtle'
                            size='xs'
                            onClick={() => handleViewSchedule(notification.scheduleId)}
                          >
                            View Schedule
                          </Button>
                        )}
                      </Group>
                    </Stack>
                  </Group>

                  <ActionIcon
                    color='red'
                    variant='subtle'
                    onClick={() => handleDeleteNotification(notification.id)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Paper>
            ))
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default NotificationPage;
